import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faDiamondExclamation,
  faMagnifyingGlass,
} from "@fortawesome/sharp-solid-svg-icons"
import {
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
  styled,
} from "@mui/material"
import type { Geotag } from "@phc/common"
import { useMemo } from "react"
import { Link as RouterLink } from "react-router"
import { useRouteAccess } from "../../hooks/useRouteAccess"
import { ROUTES } from "../../utils/constants"
import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import { GeotagsContainer } from "../GeotagsContainer"
import { useWastewaterData } from "./useWastewaterData"
import { useWastewaterContext } from "./WastewaterContext"
import type { WastewaterCategory } from "./wastewaterHeatmapConstants"
import { WASTEWATER_CHART_COLORS } from "./wastewaterHeatmapConstants"
import { getLevel, makeAnomalyKey } from "./WastewaterUtils"
import type { NewChatMessage } from "../Chat/useChatSession"
import { buildWastewaterAnomalyChatContextMessage } from "../Chat/prompts"

const AnomalyCard = styled(Paper)({
  display: "flex",
  gap: 12,
  borderRadius: 2,
  position: "relative",
  overflow: "hidden",
  width: 270,
  minHeight: 72,
  flexShrink: 0,
})

const BackgroundIcon = styled(FontAwesomeIcon)({
  position: "absolute",
  top: "-20%",
  right: -40,
  opacity: 0.1,
  width: 140,
  height: 140,
})

const Bar = styled("div")({
  width: 21,
  borderRadius: 2,
})

const BarIcon = styled(FontAwesomeIcon)({
  position: "absolute",
  bottom: 12,
  width: 32,
  height: 32,
})

const AnomalyRow = styled("div")({
  display: "grid",
  gap: 18,
  padding: "6px 12px 6px 0px",
  width: "100%",
})

const AnomalyHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
})

const AnomalyList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  paddingRight: 6,
})

export interface WastewaterAnomaly {
  id: string
  pathogen: string
  title: string
  date: string
  site: string
  geotags: Geotag[]
  value: WastewaterCategory
}

const AnomalyMessage = ({ children }: { children: string }) => (
  <Typography variant="body2" color={extraColors.medium} sx={{ minWidth: 270 }}>
    {children}
  </Typography>
)

export const WastewaterAnomalies = ({
  phcSiteId,
  limit,
  maxHeight,
}: {
  phcSiteId: string
  limit: number
  maxHeight?: number
}) => {
  const { dateRange, setSelectedAnomalyKey } = useWastewaterContext()
  const { canAccessRoute } = useRouteAccess()
  const canInvestigate = canAccessRoute("WINGMAN")
  const { pcrAnalysisData, activityLevelMap, anomalyKeys, isLoading, isError } =
    useWastewaterData({
      phcSiteId,
      limit,
      startDate: dateRange[0],
      endDate: dateRange[1],
    })

  const anomalies = useMemo(() => {
    const analysis = pcrAnalysisData ?? []
    return analysis
      .filter(item => {
        const key = makeAnomalyKey(item.target_name, item.sampling_end_date)
        return anomalyKeys.has(key)
      })
      .sort(
        (a, b) =>
          dayjsUTC(b.sampling_end_date).valueOf() -
          dayjsUTC(a.sampling_end_date).valueOf()
      )
      .map(item => {
        const key = makeAnomalyKey(item.target_name, item.sampling_end_date)
        const activityLevel = getLevel(activityLevelMap.get(key))
        return {
          id: `${item.phc_site_id}-${item.target_name}-${item.sampling_end_date}`,
          pathogen: item.target_name,
          title: `${item.target_name} Anomaly Detected`,
          date: item.sampling_end_date,
          site: item.phc_site_id,
          value: activityLevel,
          geotags: [],
        }
      })
  }, [pcrAnalysisData, activityLevelMap, anomalyKeys])

  const showList = !isLoading && !isError && anomalies.length > 0

  let renderContent
  if (isLoading) {
    renderContent = (
      <Stack spacing={2}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} height={88} width={270} />
        ))}
      </Stack>
    )
  } else if (isError) {
    renderContent = (
      <AnomalyMessage>Data is unavailable right now.</AnomalyMessage>
    )
  } else if (!anomalies.length) {
    renderContent = <AnomalyMessage>No anomalies found.</AnomalyMessage>
  } else {
    renderContent = (
      <AnomalyList>
        {anomalies.map(anomaly => {
          const colors = WASTEWATER_CHART_COLORS[anomaly.value]
          return (
            <AnomalyCard
              key={anomaly.id}
              onMouseOver={() =>
                setSelectedAnomalyKey(
                  makeAnomalyKey(anomaly.pathogen, anomaly.date)
                )
              }
              onMouseOut={() => setSelectedAnomalyKey(null)}
              style={{
                borderColor: colors.light,
                backgroundColor: colors.deep,
                cursor: "pointer",
              }}
            >
              <BackgroundIcon
                icon={faDiamondExclamation}
                color={colors.light}
              />
              <Bar style={{ backgroundColor: colors.muted }}>
                <BarIcon icon={faDiamondExclamation} color={colors.light} />
              </Bar>
              <AnomalyRow>
                <AnomalyHeader>
                  <Typography variant="body2Bold" sx={{ fontWeight: 500 }}>
                    {anomaly.title}
                  </Typography>

                  {canInvestigate && (
                    <IconButton
                      size="small"
                      component={RouterLink}
                      to={{
                        pathname: ROUTES.WINGMAN,
                        search: `?${new URLSearchParams({
                          newChatMessage: JSON.stringify({
                            title: anomaly.title,
                            query:
                              buildWastewaterAnomalyChatContextMessage(anomaly),
                          } satisfies NewChatMessage),
                        })}`,
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        color={extraColors.status.navyLight}
                      />
                    </IconButton>
                  )}
                </AnomalyHeader>
                <AnomalyHeader>
                  <Typography
                    variant="tiny"
                    color="text.secondary"
                    style={{ textTransform: "uppercase" }}
                  >
                    week ending: {anomaly.date}
                  </Typography>
                  <GeotagsContainer geotags={anomaly.geotags} />
                </AnomalyHeader>
              </AnomalyRow>
            </AnomalyCard>
          )
        })}
      </AnomalyList>
    )
  }

  return (
    <Stack
      spacing={2}
      sx={
        showList && maxHeight ? { height: maxHeight, minHeight: 0 } : undefined
      }
    >
      <Typography variant="h3">Anomalies</Typography>
      {renderContent}
    </Stack>
  )
}
